import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/apiClient";
import ParentageTree from "../lineage/ParentageTree";
import { BadgeCheck, AlertCircle, Bookmark, Mountain, Network, ExternalLink, Sparkles, Leaf, Activity } from "lucide-react";

/**
 * v3.99e — StrainAtlasWidget
 *
 * Embeds the lineage atlas content (pedigree + chemotype / morphology /
 * phenotype trait summary) directly inside the listing detail page so
 * shoppers see the full strain provenance + trait profile without
 * needing to bounce to /lineage/<slug>.
 *
 * Behavior:
 *   1. Try `/api/lineage/<slug>` where slug = slugify(strainName).
 *   2. If 404, fall back to `/api/lineage?q=<name>&limit=1` and refetch
 *      the matched record by its real slug.
 *   3. If nothing matches, render an unobtrusive empty state.
 *
 * Renders three blocks (only the ones with data):
 *   • Strain header card  — verification/breeder/save count chips
 *   • <ParentageTree compact /> — the visual genetics map
 *   • Trait matrix grid   — chemotype, morphology, phenotype
 *
 * Click on the header card → /lineage/<slug>
 */

function slugify(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export default function StrainAtlasWidget({ strainName, lineageSlug }) {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!strainName && !lineageSlug) { setLoading(false); setNotFound(true); return undefined; }
    let alive = true;
    setLoading(true);
    setNotFound(false);
    const slug = lineageSlug || slugify(strainName);
    const fetchBySlug = (s) => api.get(`/strains/${s}`).then((r) => r.data);
    const fetchByQuery = async () => {
      const r = await api.get("/lineage", { params: { q: strainName, limit: 1 } });
      const first = (r.data || [])[0];
      if (!first) return null;
      return await fetchBySlug(first.slug || first.id);
    };
    const run = async () => {
      try {
        const d = await fetchBySlug(slug).catch(() => null);
        if (d) { if (alive) setDoc(d); return; }
        // Fallback to fuzzy search by strain name.
        if (strainName) {
          const d2 = await fetchByQuery().catch(() => null);
          if (alive && d2) { setDoc(d2); return; }
        }
        if (alive) { setDoc(null); setNotFound(true); }
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [strainName, lineageSlug]);

  if (loading) {
    return (
      <section className="bg-[var(--surface)] border border-[var(--border)] p-5 mb-6"
               data-testid="strain-atlas-widget-loading">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em]"
             style={{ color: "var(--text-muted)" }}>
          Loading strain atlas…
        </div>
      </section>
    );
  }

  if (notFound || !doc) {
    return (
      <section className="bg-[var(--surface)] border border-dashed border-[var(--border)] p-5 mb-6 text-center"
               data-testid="strain-atlas-widget-empty">
        <div className="label-eyebrow mb-2" style={{ color: "var(--text-muted)" }}>
          Strain Atlas
        </div>
        <p className="text-sm" style={{ color: "var(--text-2)" }}>
          No verified lineage record on file for <strong style={{ color: "var(--text)" }}>{strainName}</strong>.
        </p>
        <Link to={`/lineage?suggest=${encodeURIComponent(strainName || "")}`}
              className="inline-flex items-center gap-1 mt-3 text-[10px] font-mono uppercase tracking-[0.18em]"
              style={{ color: "var(--gold)" }}
              data-testid="strain-atlas-widget-suggest">
          Submit lineage →
        </Link>
      </section>
    );
  }

  return (
    <div data-testid="strain-atlas-widget">
      <StrainHeaderCard doc={doc} />
      <ParentageTree doc={doc} compact />
      <TraitMatrixCard doc={doc} />
    </div>
  );
}

function StrainHeaderCard({ doc }) {
  const breeder = (doc.related_breeders || [])[0];
  return (
    <Link to={`/strains/${doc.slug || doc.id}`}
          className="block bg-[var(--surface)] border border-[var(--border)] p-5 mb-3 hover:border-[var(--gold)] transition-colors group"
          data-testid="strain-atlas-header-card">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="label-eyebrow flex items-center gap-2" style={{ color: "var(--text-muted)" }}>
          <Network className="w-3 h-3" style={{ color: "var(--gold)" }} />
          Strain Atlas
          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-[10px]"
                style={{ color: "var(--gold)" }}>
            · open lineage page →
          </span>
        </div>
        <ExternalLink className="w-3.5 h-3.5 shrink-0"
                      style={{ color: "var(--text-muted)" }} />
      </div>

      <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono uppercase tracking-[0.18em] mb-2"
           style={{ color: "var(--text-muted)" }}>
        {doc.verification_status === "verified" && (
          <span className="inline-flex items-center gap-1" style={{ color: "var(--primary-text)" }}>
            <BadgeCheck className="w-3 h-3" /> Verified record
          </span>
        )}
        {doc.verification_status === "disputed" && (
          <span className="inline-flex items-center gap-1" style={{ color: "var(--secondary)" }}>
            <AlertCircle className="w-3 h-3" /> Disputed
          </span>
        )}
        {doc.landrace && (
          <span className="inline-flex items-center gap-1" style={{ color: "var(--gold)" }}>
            <Mountain className="w-3 h-3" /> Landrace
          </span>
        )}
        {doc.heirloom && <span style={{ color: "var(--gold)" }}>Heirloom</span>}
        {doc.preservation && <span style={{ color: "var(--gold)" }}>Preservation</span>}
        {(doc.save_count > 0) && (
          <span className="inline-flex items-center gap-1" style={{ color: "var(--gold)" }}>
            <Bookmark className="w-3 h-3" /> {doc.save_count} saved
          </span>
        )}
      </div>

      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="font-display text-2xl md:text-3xl tracking-tighter leading-tight"
               style={{ color: "var(--text)" }}>
            {doc.strain_name}
          </div>
          {breeder && (
            <div className="text-[11px] font-mono mt-1" style={{ color: "var(--text-muted)" }}>
              originated by{" "}
              <span style={{ color: "var(--gold)" }}>{breeder.store_name}</span>
            </div>
          )}
        </div>
        <div className="text-[10px] font-mono uppercase tracking-[0.18em]"
             style={{ color: "var(--text-muted)" }}>
          {doc.classification && (
            <span style={{ color: "var(--text-2)" }}>
              {String(doc.classification).replace(/_/g, " ")}
            </span>
          )}
          {doc.seed_type && (
            <span> · {String(doc.seed_type).replace(/_/g, " ")}</span>
          )}
        </div>
      </div>
    </Link>
  );
}

// v3.99e — Per-strain trait matrix card. Surfaces the chemotype +
// morphology + phenotype fields from the lineage record as a small
// 3-column grid. Each block hides if no data is recorded — empty grids
// don't render.
function TraitMatrixCard({ doc }) {
  const chemo = doc.chemotype || {};
  const morph = doc.morphology || {};
  const pheno = doc.phenotype || {};

  const chemoStats = [
    chemo.dominant_cannabinoid && ["Dominant", String(chemo.dominant_cannabinoid).toUpperCase()],
    chemo.thc_range && ["THC", chemo.thc_range],
    chemo.cbd_range && ["CBD", chemo.cbd_range],
    chemo.terpene_profile && ["Terpenes", Array.isArray(chemo.terpene_profile) ? chemo.terpene_profile.join(", ") : chemo.terpene_profile],
    chemo.dominant_terpenes && ["Dominant terps", Array.isArray(chemo.dominant_terpenes) ? chemo.dominant_terpenes.join(", ") : chemo.dominant_terpenes],
  ].filter(Boolean);

  const morphStats = [
    morph.plant_height && ["Height", String(morph.plant_height).replace(/_/g, " ")],
    morph.plant_structure && ["Structure", String(morph.plant_structure).replace(/_/g, " ")],
    morph.leaf_morphology && ["Leaf", String(morph.leaf_morphology).replace(/_/g, " ")],
    morph.bud_density && ["Bud density", String(morph.bud_density).replace(/_/g, " ")],
    morph.bud_color && ["Color", morph.bud_color],
    morph.trichome_coverage && ["Trichomes", String(morph.trichome_coverage).replace(/_/g, " ")],
  ].filter(Boolean);

  const phenoStats = [
    pheno.flowering_time && ["Flowering", pheno.flowering_time],
    pheno.yield_indoor && ["Yield indoor", pheno.yield_indoor],
    pheno.yield_outdoor && ["Yield outdoor", pheno.yield_outdoor],
    pheno.difficulty && ["Difficulty", String(pheno.difficulty).replace(/_/g, " ")],
    pheno.resilience && ["Resilience", String(pheno.resilience).replace(/_/g, " ")],
    pheno.environment && ["Environment", String(pheno.environment).replace(/_/g, " ")],
  ].filter(Boolean);

  const anyData = chemoStats.length || morphStats.length || phenoStats.length;
  if (!anyData) return null;

  return (
    <section className="bg-[var(--surface)] border border-[var(--border)] p-4 md:p-5 mb-6"
             data-testid="strain-atlas-trait-matrix">
      <div className="label-eyebrow flex items-center gap-2 mb-4" style={{ color: "var(--text-muted)" }}>
        <span style={{ color: "var(--gold)" }}>●</span> Trait Matrix
        <span className="hidden md:inline text-[10px] opacity-60">· chemotype · morphology · phenotype</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {chemoStats.length > 0 && (
          <TraitBlock icon={Sparkles} title="Chemotype" stats={chemoStats}
                      testid="strain-atlas-trait-chemotype" />
        )}
        {morphStats.length > 0 && (
          <TraitBlock icon={Leaf} title="Morphology" stats={morphStats}
                      testid="strain-atlas-trait-morphology" />
        )}
        {phenoStats.length > 0 && (
          <TraitBlock icon={Activity} title="Phenotype" stats={phenoStats}
                      testid="strain-atlas-trait-phenotype" />
        )}
      </div>
    </section>
  );
}

function TraitBlock({ icon: Icon, title, stats, testid }) {
  return (
    <div className="bg-[var(--surface-2)] border border-[var(--border)] p-3"
         data-testid={testid}>
      <div className="flex items-center gap-1.5 mb-2 text-[10px] font-mono uppercase tracking-[0.18em]"
           style={{ color: "var(--gold)" }}>
        <Icon className="w-3 h-3" /> {title}
      </div>
      <ul className="space-y-1">
        {stats.map(([k, v]) => (
          <li key={k} className="text-xs flex items-baseline justify-between gap-2 border-b border-dashed border-[var(--border)] pb-1 last:border-b-0">
            <span className="font-mono uppercase tracking-[0.14em] text-[10px]"
                  style={{ color: "var(--text-muted)" }}>
              {k}
            </span>
            <span className="text-right truncate" style={{ color: "var(--text)" }}>
              {v}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
